/**
 * Laporan balik Telegram → draft → konfirmasi 1 klik (inventaris A13; KEPUTUSAN_FINAL 30 Jul butir K):
 * "Laporan balik PIC dibaca sistem → jadi DRAFT → Admin Produksi konfirmasi 1 klik → baru update WO".
 * Usulan tindakan dihitung backend dari jenis laporan + status tahap; layar tidak menebak.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatDateTime } from "../lib/format";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, Paginasi } from "../components/etams/PalangDaftar";

const KATA_USULAN = {
  MULAI: "Mulai tahap",
  SELESAIKAN: "Selesaikan tahap",
  MULAI_SELESAIKAN: "Mulai lalu selesaikan tahap",
  CATAT: "Catat saja (WO tidak berubah)",
};
const KATA_JENIS = { TERIMA: "Terima tugas", SELESAI: "Selesai", KENDALA: "Kendala" };
const KOLOM_CSV = [
  { kunci: "dilaporkan_pada", judul: "Dilaporkan" },
  { kunci: "jenis", judul: "Jenis" },
  { kunci: "wo_number", judul: "WO" },
  { kunci: "stage_code", judul: "Tahap" },
  { kunci: "status_tahap", judul: "Status tahap" },
  { kunci: "pelapor", judul: "Pelapor" },
  { kunci: "catatan", judul: "Catatan" },
  { kunci: "usulan", judul: "Usulan" },
];

export default function LaporanBalik() {
  // ⛔ Dulu server memotong 200 baris tanpa tanda (BACKLOG LAPORAN-BALIK-TERPOTONG-200-01).
  //    Kini berhalaman di server: `total` = jumlah laporan menunggu yang berhak dilihat.
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [memuat, setMemuat] = useState(true);
  const [sibuk, setSibuk] = useState(null);
  // Cermin `URUT_SAH` di services/laporan_balik.py.
  const daftar = useDaftar(items, {
    server: { total, urutSah: ["dilaporkan_pada", "jenis"] },
    urutAwal: { kunci: "dilaporkan_pada", arah: "desc" },
    saringan: { jenis: { label: "Jenis", param: "jenis", tampil: (v) => KATA_JENIS[v] || v } },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const nilaiJenis = daftar.nilaiSaringan.jenis || "";

  const bangunParams = useCallback((o = {}) => ({ ...paramSaringan, ...(cari && { q: cari }),
    limit, offset, sort, arah, ...o }), [paramSaringan, cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get("/laporan-balik", { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [bangunParams]);

  const muat = useCallback(async () => {
    setMemuat(true);
    try {
      const r = await api.get("/laporan-balik", { params: bangunParams() });
      setItems(r.data.items || []);
      setTotal(typeof r.data.total === "number" ? r.data.total : (r.data.items || []).length);
    } catch (e) { toast.error(apiErrorMessage(e, "Laporan balik gagal dimuat.")); setItems([]); setTotal(0); }
    finally { setMemuat(false); }
  }, [bangunParams]);
  useEffect(() => { muat(); }, [muat]);

  const putus = async (x, terapkan) => {
    setSibuk(x.id);
    try {
      await api.post(`/laporan-balik/${x.id}/konfirmasi`, { terapkan });
      toast.success(terapkan ? "Laporan diterapkan ke WO." : "Laporan ditandai sudah dibaca.");
      await muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Konfirmasi gagal.")); }
    finally { setSibuk(null); }
  };

  return (
    <div className="space-y-4" data-testid="laporan-balik-page">
      <div>
        <h1 className="text-xl font-semibold">Laporan Balik Telegram</h1>
        <p className="text-sm text-muted-foreground">
          Laporan Terima / Selesai / Kendala dari personil lewat Telegram. Terapkan untuk memperbarui tahap WO,
          atau abaikan bila tidak sesuai kenyataan.
        </p>
      </div>
      <PalangDaftar daftar={daftar} placeholder="Cari catatan laporan…" testId="laporan-balik-palang"
                    ekspor={{ nama: "laporan-balik", kolom: KOLOM_CSV, ambilHalaman }}>
        <select className="h-9 rounded-md border bg-background px-2 text-sm" value={nilaiJenis}
                onChange={(e) => daftar.setSaringan("jenis", e.target.value)} data-testid="laporan-balik-saring-jenis"
                aria-label="Saring jenis laporan">
          <option value="">Semua jenis</option>
          {Object.entries(KATA_JENIS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </PalangDaftar>
      {memuat && items.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="laporan-balik-memuat">Memuat…</div>
      ) : daftar.total === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="laporan-balik-kosong">
          {daftar.cari.trim() || daftar.saringanAktif?.length ? "Tidak ada yang cocok." : "Tidak ada laporan yang menunggu konfirmasi."}
        </Card>
      ) : (
        <div className="space-y-2">
          {daftar.tampil.map((x) => (
            <Card key={x.id} className="p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`laporan-balik-${x.id}`}>
              <div className="space-y-1 text-sm">
                <div className="font-medium">
                  {KATA_JENIS[x.jenis] || x.jenis} · {x.wo_id ? <Link className="underline" to={`/wo/${x.wo_id}`}>{x.wo_number}</Link> : "—"} · {x.stage_code || "—"}
                </div>
                <div className="text-muted-foreground">
                  {x.pelapor || "Pelapor tidak dikenali"} · {formatDateTime(x.dilaporkan_pada)} · status tahap {x.status_tahap || "—"}
                </div>
                {x.catatan && <div>“{x.catatan}”</div>}
                <div className="text-xs">Usulan: <span className="font-medium">{KATA_USULAN[x.usulan] || x.usulan}</span></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" disabled={sibuk === x.id} onClick={() => putus(x, true)} data-testid={`laporan-balik-terapkan-${x.id}`}>
                  {x.usulan === "CATAT" ? "Tandai dibaca" : "Terapkan"}
                </Button>
                {x.usulan !== "CATAT" && (
                  <Button size="sm" variant="outline" disabled={sibuk === x.id} onClick={() => putus(x, false)}
                          data-testid={`laporan-balik-abaikan-${x.id}`}>Abaikan</Button>
                )}
              </div>
            </Card>
          ))}
          <Paginasi daftar={daftar} testId="laporan-balik-paginasi" />
        </div>
      )}
    </div>
  );
}
